// What state is an agent's phone (Daksha Caller) in, and WHY — in words the
// agent can act on. Pure functions over callappPresence/{uid}; no Firebase
// imports, so the same rules drive the header pill, the dial card and the
// manager's agent-phones bar, and are unit-tested.
//
// Shape written by the phone (v3.11+):
//   beat    server timestamp, every 30 s while the service runs
//   device  { at, state: online|stopped|signed_out, linkUp, net, netValidated,
//             airplane, sim, service, signal, battery, charging, powerSave,
//             batteryOptExempt, bgRestricted, perms: {...}, versionCode,
//             versionName, protocol, clockSkewMs, lastOutage: {from,to,reason} }
// Older builds only leave { agent, lastSeen, linkUp, appVer } via the proxy.

// Keep in step with public/daksha-caller.json (a test enforces it).
export const LATEST_APP_VERSION_CODE = 23;
export const LATEST_APP_VERSION_NAME = '4.30';

// The phone beats every 30 s; three missed beats means it is gone. Legacy
// builds only ping every 5 minutes, so they get a window wider than that.
export const BEAT_STALE_MS = 90_000;
export const LEGACY_STALE_MS = 6 * 60_000;

export const lastContact = (p) =>
    Math.max(Number(p?.lastSeen) || 0, Number(p?.beat) || 0, Number(p?.device?.at) || 0);

const hhmm = (ts) => new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
const dayHhmm = (ts, now) => {
    const sameDay = new Date(ts + 5.5 * 3600_000).toISOString().slice(0, 10) === new Date(now + 5.5 * 3600_000).toISOString().slice(0, 10);
    return sameDay ? hhmm(ts)
        : `${new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })} ${hhmm(ts)}`;
};

/**
 * Problems visible in a device snapshot, most serious first. `blocks` marks the
 * ones that stop a call from being placed at all.
 */
export function deviceProblems(dev) {
    if (!dev) return [];
    const out = [];
    const perms = dev.perms || {};
    if (dev.airplane) out.push({ code: 'airplane', blocks: true, text: 'Airplane mode is on' });
    if (dev.net === 'none') out.push({ code: 'no_internet', blocks: true, text: 'No internet on the phone (Wi-Fi and mobile data are both off or out of range)' });
    else if (dev.netValidated === false) out.push({ code: 'internet_broken', blocks: true, text: `Phone is on ${dev.net === 'wifi' ? 'Wi-Fi' : 'mobile data'} but the internet is not working` });
    if (dev.sim && dev.sim !== 'ready') out.push({ code: 'sim', blocks: true, text: dev.sim === 'absent' ? 'No SIM card in the phone' : 'SIM card is not ready' });
    if (dev.service && dev.service !== 'in_service') {
        out.push({ code: 'no_signal', blocks: true, text: dev.service === 'emergency_only' ? 'No mobile signal — emergency calls only' : 'No mobile network signal — calls cannot go out' });
    } else if (dev.signal === 0) {
        out.push({ code: 'weak_signal', blocks: false, text: 'Very weak mobile signal — calls may drop' });
    }
    if (dev.simChoiceNeeded) out.push({ code: 'sim_choice', blocks: false, text: 'Dual SIM with no default for calls — the phone stops to ask which SIM on every call' });
    if (perms.callPhone === false) out.push({ code: 'perm_call', blocks: true, text: 'Permission "Phone calls" is not granted in Daksha Caller' });
    if (perms.phoneState === false) out.push({ code: 'perm_state', blocks: false, text: 'Permission "Phone state" missing — call end will not be reported' });
    if (perms.callLog === false) out.push({ code: 'perm_log', blocks: false, text: 'Permission "Call log" missing — calls will not sync' });
    if (perms.notifications === false) out.push({ code: 'perm_notif', blocks: false, text: 'Notifications are blocked — the customer card cannot pop up' });
    // Caller 4.30+: the FCM push that wakes a sleeping phone for a desk call.
    if (dev.push === false) out.push({ code: 'no_push', blocks: false, text: 'Backup wake-up is not set up on the phone — calls from the system may wait until the app reconnects (open Daksha Caller once with internet)' });
    if (dev.batteryOptExempt === false) out.push({ code: 'battery_opt', blocks: false, text: 'Battery optimisation is ON for Daksha Caller — the phone may stop it in the background' });
    if (dev.bgRestricted) out.push({ code: 'bg_restricted', blocks: false, text: 'Background use is restricted for Daksha Caller' });
    if (dev.powerSave) out.push({ code: 'power_save', blocks: false, text: 'Battery saver is on' });
    if (Number.isFinite(dev.battery) && dev.battery <= 10 && !dev.charging) out.push({ code: 'battery_low', blocks: false, text: `Battery low (${dev.battery}%)` });
    if (Math.abs(Number(dev.clockSkewMs) || 0) > 2 * 60_000) {
        out.push({ code: 'clock', blocks: false, text: `Phone clock is wrong by about ${Math.round(Math.abs(dev.clockSkewMs) / 60_000)} min — turn on automatic date & time` });
    }
    if (dev.versionCode && dev.versionCode < LATEST_APP_VERSION_CODE) {
        out.push({ code: 'outdated', blocks: false, text: `Old app version (v${dev.versionName || dev.versionCode}) — update to v${LATEST_APP_VERSION_NAME}` });
    }
    return out;
}

/**
 * One verdict for a presence record.
 *
 * @returns {{
 *   status: 'none'|'online'|'warning'|'offline',
 *   headline: string,            // short, for a pill
 *   reasons: {code,text,blocks}[], // why (for offline: the LAST KNOWN conditions)
 *   since: number,               // offline: last contact
 *   canDial: boolean,
 * }}
 */
export function describePhone(p, now = Date.now()) {
    if (!p || !lastContact(p)) {
        return { status: 'none', headline: 'No phone connected', reasons: [{ code: 'never', blocks: true, text: 'Sign in to Daksha Caller on your phone' }], since: 0, canDial: false };
    }
    const dev = p.device || null;
    const last = lastContact(p);
    const beat = Number(p.beat) || 0;

    // Said goodbye explicitly — the most precise reason there is.
    if (dev && (dev.state === 'stopped' || dev.state === 'signed_out') && (Number(dev.at) || 0) >= beat) {
        const text = dev.state === 'signed_out' ? 'Daksha Caller was signed out on the phone' : 'Daksha Caller was closed / stopped on the phone';
        return {
            status: 'offline', headline: `Phone offline since ${dayHhmm(dev.at, now)}`,
            reasons: [{ code: dev.state, blocks: true, text }], since: Number(dev.at) || last, canDial: false,
        };
    }

    const window = (beat || dev) ? BEAT_STALE_MS : LEGACY_STALE_MS;
    const problems = deviceProblems(dev);
    if (now - last > window) {
        // Silent phone. It cannot tell us why while it is silent, so say what it
        // last reported and the usual causes, honestly labelled as likely.
        const reasons = [];
        const lastNet = problems.filter(x => ['no_internet', 'internet_broken', 'airplane'].includes(x.code));
        reasons.push(...lastNet);
        if (!lastNet.length) {
            reasons.push({ code: 'silent', blocks: true, text: 'The phone stopped checking in — most likely no internet, or the phone closed the app to save battery' });
        }
        reasons.push(...problems.filter(x => ['battery_opt', 'bg_restricted', 'power_save', 'battery_low'].includes(x.code)));
        if (!dev) reasons.push({ code: 'outdated', blocks: false, text: `Old app version — update to v${LATEST_APP_VERSION_NAME} to see live phone health` });
        return { status: 'offline', headline: `Phone offline since ${dayHhmm(last, now)}`, reasons, since: last, canDial: false };
    }

    const linkUp = dev && typeof dev.linkUp === 'boolean' ? dev.linkUp : p.linkUp;
    if (linkUp === false) {
        return {
            status: 'offline', headline: 'Phone reconnecting…',
            reasons: [{ code: 'link_down', blocks: true, text: 'The app is running but its call link to the system is reconnecting' }, ...problems],
            since: last, canDial: false,
        };
    }
    const blocking = problems.filter(x => x.blocks);
    if (blocking.length) {
        return { status: 'warning', headline: blocking[0].text, reasons: problems, since: last, canDial: false };
    }
    if (problems.length) {
        return { status: 'warning', headline: 'Phone connected — needs attention', reasons: problems, since: last, canDial: true };
    }
    return { status: 'online', headline: 'Phone connected', reasons: [], since: last, canDial: true };
}

// ── Dial command progress ────────────────────────────────────────────────────
// tc/commands/{uid}/{cmdId}: { status: queued|acked|cancelled|failed, phone: { stage, at, reason, ... } }
// `status` is the server's view; `phone.stage` is the handset's own report.

export const DIAL_STEPS = [
    { key: 'sent', label: 'Sent to your phone' },
    { key: 'received', label: 'Phone received it' },
    { key: 'dialing', label: 'Phone is dialling' },
    { key: 'in_progress', label: 'Call active on the phone' },
    { key: 'ended', label: 'Call ended' },
];

const FAIL_TEXT = {
    expired: 'The phone got the command too late (over a minute old) and did not dial',
};

/**
 * Per-step display state for DIAL_STEPS: 'done' | 'active' | 'failed' | 'todo'.
 * `silent` = the ack window passed with no word from the phone.
 */
export function stepStates(prog, silent = false) {
    const keys = DIAL_STEPS.map(s => s.key);
    if (prog.failed || silent) {
        const lastDone = prog.acked ? 1 : 0;
        return keys.map((_, i) => (i <= lastDone ? 'done' : i === lastDone + 1 ? 'failed' : 'todo'));
    }
    const r = Math.max(0, keys.indexOf(prog.step));
    return keys.map((_, i) => (i <= r ? 'done' : i === r + 1 ? 'active' : 'todo'));
}

/**
 * How the command reached the phone (Caller 4.30+ writes it with "received"):
 * via 'rtdb' (the live stream) or 'fcm' (the push that woke the phone), and how
 * long it took from the desk's click, in ms.
 */
export function deliveryOf(ph) {
    const via = ph && (ph.via === 'fcm' || ph.via === 'rtdb') ? ph.via : '';
    const ms = Number(ph?.latencyMs);
    return { via, latencyMs: Number.isFinite(ms) && ms >= 0 && ms < 10 * 60_000 ? Math.round(ms) : null };
}

/** "via live link · 0.4 s" / "via push · 2.1 s" — '' when the phone did not say. */
export function deliveryText({ via, latencyMs } = {}) {
    if (!via) return '';
    const road = via === 'fcm' ? 'via push (phone was asleep)' : 'via live link';
    return latencyMs == null ? road : `${road} · ${(latencyMs / 1000).toFixed(1)} s`;
}

/** Fold a command node into { step, failed, reason, acked }. */
export function dialProgress(cmd) {
    if (!cmd) return { step: 'sent', failed: false, reason: '', acked: false };
    const ph = cmd.phone || {};
    const stage = ph.stage || '';
    if (stage === 'failed' || stage === 'expired') {
        return { step: stage, failed: true, reason: ph.reason || FAIL_TEXT[stage] || 'The phone could not place the call', acked: true };
    }
    if (cmd.status === 'cancelled') return { step: 'cancelled', failed: true, reason: 'Call cancelled — the phone never confirmed it', acked: false };
    if (cmd.status === 'failed') return { step: 'failed', failed: true, reason: cmd.error || 'The phone could not place the call', acked: false };
    if (['received', 'dialing', 'in_progress', 'ended'].includes(stage)) {
        return { step: stage, failed: false, reason: '', acked: true, talkSec: ph.talkSec, answered: ph.answered, ...deliveryOf(ph) };
    }
    if (cmd.status === 'acked') return { step: 'received', failed: false, reason: '', acked: true };
    return { step: 'sent', failed: false, reason: '', acked: false };
}
